import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabaseClient.js';
import { 
  FaBox, FaEnvelope, FaSignOutAlt, FaUserShield, 
  FaPlus, FaEdit, FaTrash, FaSave, FaTimes, 
  FaCheck, FaEnvelopeOpen, FaUpload, FaSpinner, FaImage, FaNewspaper,
  FaSearch, FaChartPie, FaBell, FaList, FaBars, FaCertificate,
  FaStar, FaRegStar, FaTags, FaSortNumericDown,
  FaArrowLeft, FaArrowRight, FaImages
} from 'react-icons/fa';
import "./AdminPage.css";
import PropTypes from 'prop-types';
import { getNewsImages } from '../newsImages.js';

// ==========================================
// ฟังก์ชันแปลงวันที่เป็นภาษาไทย
// ==========================================
const THAI_MONTHS = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];

function formatThaiDate(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  if (isNaN(d)) return isoDate;
  const day = String(d.getDate()).padStart(2, '0');
  const month = THAI_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

// ==========================================
// [เพิ่ม] Component Lightbox สำหรับดูรูปเต็ม
// ==========================================
function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!src) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '16px', right: '20px',
          background: 'none', border: 'none', color: '#fff',
          fontSize: '28px', cursor: 'pointer', lineHeight: 1,
        }}
        aria-label="ปิด"
      >
        <FaTimes />
      </button>
      <img
        src={src}
        alt="full"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: '90vh',
          borderRadius: '8px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          cursor: 'default',
        }}
      />
    </div>
  );
}
ImageLightbox.propTypes = { src: PropTypes.string, onClose: PropTypes.func };

// ==========================================
// ส่วนหลัก: AdminPage Layout
// ==========================================
export default function AdminPage() {
  const [session, setSession] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
// เพิ่มฟังก์ชันนี้เข้าไป
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      alert('Logout Error: ' + error.message);
    } else {
      setSession(null);
      window.location.reload(); 
    }
  };
  
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  if (loading) return <div className="loading-screen">กำลังโหลด...</div>;
  if (!session) return <LoginForm />;

  return (
    <div className="admin-layout">
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu"><FaBars size={24} /></button>

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <FaUserShield size={32} />
          <h2>Admin Panel</h2>
          <button className="close-sidebar-btn" onClick={() => setSidebarOpen(false)}><FaTimes /></button>
        </div>
        <nav className="sidebar-menu">
          <button className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => handleTabChange('dashboard')}><FaChartPie /> ภาพรวมระบบ</button>
          <button className={`menu-item ${activeTab === 'products' ? 'active' : ''}`} onClick={() => handleTabChange('products')}><FaBox /> จัดการสินค้า</button>
          <button className={`menu-item ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => handleTabChange('categories')}><FaTags /> จัดการหมวดหมู่</button>
          <button className={`menu-item ${activeTab === 'certificates' ? 'active' : ''}`} onClick={() => handleTabChange('certificates')}><FaCertificate /> ใบรับรอง ISO</button>
          <button className={`menu-item ${activeTab === 'news' ? 'active' : ''}`} onClick={() => handleTabChange('news')}><FaNewspaper /> ข่าวสาร/กิจกรรม</button>
          <button className={`menu-item ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => handleTabChange('messages')}><FaEnvelope /> ข้อความลูกค้า</button>
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn"><FaSignOutAlt /> ออกจากระบบ</button>
        </div>
      </aside>

      <main className="admin-content">
        {activeTab === 'dashboard' && <DashboardOverview setActiveTab={handleTabChange} />}
        {activeTab === 'products' && <ProductManager />}
        {activeTab === 'categories' && <CategoryManager />}
        {activeTab === 'certificates' && <CertificateManager />} 
        {activeTab === 'news' && <NewsManager />}
        {activeTab === 'messages' && <MessageViewer />}
      </main>
    </div>
  );
}

function DashboardOverview({ setActiveTab }) {
  const [stats, setStats] = useState({ products: 0, news: 0, messages: 0, unreadMessages: 0, certificates: 0 });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [productsCount, newsCount, certsCount, msgsCount, unreadCount, recentMsgs] = await Promise.all([
        supabase.from('products').select('*', { count: 'exact', head: true }),
        supabase.from('news_activities').select('*', { count: 'exact', head: true }),
        supabase.from('certificates').select('*', { count: 'exact', head: true }), 
        supabase.from('contact_messages').select('*', { count: 'exact', head: true }),
        supabase.from('contact_messages').select('*', { count: 'exact', head: true }).eq('is_read', false),
        supabase.from('contact_messages').select('*').order('created_at', { ascending: false }).limit(5)
      ]);

      setStats({
        products: productsCount.count || 0,
        news: newsCount.count || 0,
        certificates: certsCount.count || 0,
        messages: msgsCount.count || 0,
        unreadMessages: unreadCount.count || 0
      });
      if (recentMsgs.error) {
        console.error("Error fetching messages:", recentMsgs.error.message);
      } else {
        console.log("Raw Messages Data:", recentMsgs.data); // ดูใน Console ว่ามี Array ส่งมาไหม
        setRecentMessages(recentMsgs.data || []);
      }
      if (recentMsgs.data) setRecentMessages(recentMsgs.data);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="section-container"><p>กำลังประมวลผลข้อมูล...</p></div>;

  return (
    <div className="section-container fade-in">
      <div className="section-header"><h2>ภาพรวมระบบ (Dashboard)</h2></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="dash-card" style={{borderLeft: '5px solid #1d4ed8'}}><FaBox size={30} color="#1d4ed8" /><div><h3>{stats.products}</h3><p>สินค้าทั้งหมด</p></div></div>
        <div className="dash-card" style={{borderLeft: '5px solid #9333ea'}}><FaCertificate size={30} color="#9333ea" /><div><h3>{stats.certificates}</h3><p>ใบรับรอง ISO</p></div></div>
        <div className="dash-card" style={{borderLeft: '5px solid #15803d'}}><FaNewspaper size={30} color="#15803d" /><div><h3>{stats.news}</h3><p>ข่าวสาร</p></div></div>
        <div className="dash-card" style={{borderLeft: '5px solid #be123c'}}><FaBell size={30} color="#be123c" /><div><h3>{stats.unreadMessages}</h3><p>ข้อความใหม่</p></div></div>
      </div>
      <div className="card-box">
        <h3><FaList /> ข้อความล่าสุด <button className="btn-sm btn-outline" onClick={() => setActiveTab('messages')}>ดูทั้งหมด</button></h3>
        <table className="data-table">
          <thead><tr><th>สถานะ</th><th>วันที่</th><th>ผู้ส่ง</th><th>เรื่อง</th></tr></thead>
          <tbody>
            {recentMessages.length > 0 ? recentMessages.map(msg => (
              <tr key={msg.id}>
                <td>{msg.is_read ? <span className="badge-gray">อ่านแล้ว</span> : <span className="badge-red">ใหม่</span>}</td>
                <td>{new Date(msg.created_at).toLocaleDateString('th-TH')}</td>
                <td>{msg.name}</td>
                <td>{msg.subject}</td>
              </tr>
            )) : <tr><td colSpan="4" style={{textAlign:'center'}}>ยังไม่มีข้อความ</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
DashboardOverview.propTypes = { setActiveTab: PropTypes.func };

function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) alert(error.message);
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header"><FaUserShield size={40} color="#2563eb" /><h1>Admin Login</h1></div>
        <form onSubmit={handleLogin}>
          <div className="input-group"><label>อีเมล</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
          <div className="input-group"><label>รหัสผ่าน</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} required /></div>
          <button type="submit" disabled={loading} className="login-btn">{loading ? 'กำลังตรวจสอบ...' : 'เข้าสู่ระบบ'}</button>
        </form>
      </div>
    </div>
  );
}

function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newMainCategory, setNewMainCategory] = useState('');
  const [mainPriority, setMainPriority] = useState(999);
  const [loading, setLoading] = useState(false);

  // State สำหรับจัดการลำดับของกลุ่มหลัก (Main Categories)
  const [mainCategoryPriorities, setMainCategoryPriorities] = useState({});

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('main_priority', { ascending: true })
      .order('name', { ascending: true });
    
    if (!error) {
      setCategories(data);
      
      // ดึงรายชื่อกลุ่มหลักและลำดับปัจจุบันออกมาเก็บไว้
      const priorities = {};
      data.forEach(cat => {
        if (cat.main_category) {
          priorities[cat.main_category] = cat.main_priority || 999;
        }
      });
      setMainCategoryPriorities(priorities);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // 🟢 ฟังก์ชันบันทึกลำดับของ "กลุ่มหลัก" ทั้งหมด
  const handleUpdateMainPriorities = async () => {
    setLoading(true);
    try {
      // วนลูปอัปเดตทุกหมวดหมู่ที่อยู่ในกลุ่มหลักนั้นๆ ให้มี main_priority เท่ากัน
      for (const [mainCat, priority] of Object.entries(mainCategoryPriorities)) {
        await supabase
          .from('categories')
          .update({ main_priority: parseInt(priority) })
          .eq('main_category', mainCat);
      }
      alert('อัปเดตลำดับหมวดหมู่หลักเรียบร้อยแล้ว');
      fetchCategories();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCategory.trim() || !newMainCategory.trim()) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    setLoading(true);
    
    // ใช้ลำดับจากกลุ่มหลักที่มีอยู่แล้ว (ถ้ามี)
    const currentPriority = mainCategoryPriorities[newMainCategory.trim()] || mainPriority;

    const categoryData = { 
      name: newCategory.trim(), 
      main_category: newMainCategory.trim(),
      main_priority: parseInt(currentPriority)
    };

    if (isEditing) {
      const { error } = await supabase.from('categories').update(categoryData).eq('id', editId);
      if (!error) { alert('แก้ไขสำเร็จ'); resetForm(); fetchCategories(); }
    } else {
      const { error } = await supabase.from('categories').insert([categoryData]);
      if (!error) { resetForm(); fetchCategories(); }
    }
    setLoading(false);
  };

  const startEdit = (cat) => {
    setIsEditing(true);
    setEditId(cat.id);
    setNewCategory(cat.name);
    setNewMainCategory(cat.main_category || '');
    setMainPriority(cat.main_priority || 999);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setNewCategory(''); setNewMainCategory(''); setMainPriority(999);
    setIsEditing(false); setEditId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('ยืนยันการลบ?')) {
      await supabase.from('categories').delete().eq('id', id);
      fetchCategories();
    }
  };

  const groupedCategories = categories.reduce((acc, cat) => {
    const main = cat.main_category || 'อื่นๆ';
    if (!acc[main]) acc[main] = [];
    acc[main].push(cat);
    return acc;
  }, {});

  return (
    <div className="section-container">
      <div className="section-header">
        <h2>จัดการลำดับหมวดหมู่หลัก</h2>
        <button className="btn-primary" onClick={handleUpdateMainPriorities} disabled={loading}>
          <FaSave /> บันทึกลำดับกลุ่มหลัก
        </button>
      </div>
      
      {/* ส่วนตารางจัดการลำดับกลุ่มหลัก (Quick Edit) */}
      <div className="card-box" style={{ marginBottom: '30px', padding: '20px', background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <h4 style={{ marginBottom: '15px', color: '#0369a1' }}><FaSortNumericDown /> ตั้งลำดับการแสดงผลของกลุ่มหลัก</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
          {Object.keys(mainCategoryPriorities).map(mainCat => (
            <div key={mainCat} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <input 
                type="number" 
                value={mainCategoryPriorities[mainCat]} 
                onChange={(e) => setMainCategoryPriorities({ ...mainCategoryPriorities, [mainCat]: e.target.value })}
                style={{ width: '50px', padding: '5px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold' }}
              />
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{mainCat}</span>
            </div>
          ))}
        </div>
        <small style={{ display: 'block', marginTop: '10px', color: '#64748b' }}>* เลขน้อยจะขึ้นก่อนในหน้าสินค้า / กดบันทึกด้านบนเพื่อเริ่มใช้งาน</small>
      </div>

      <div className="section-header">
        <h2>เพิ่ม/แก้ไข หมวดหมู่ย่อย</h2>
      </div>

      <div className="form-card" style={{ marginBottom: '20px', border: isEditing ? '2px solid #0ea5e9' : 'none' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ fontSize: '0.8rem' }}>หมวดหมู่หลัก (เลือกจากด้านล่างหรือพิมพ์ใหม่)</label>
            <input type="text" value={newMainCategory} onChange={(e) => setNewMainCategory(e.target.value)} placeholder="เช่น อุปกรณ์ตรวจวัด" style={{ width: '100%', padding: '10px' }} required />
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ fontSize: '0.8rem' }}>หมวดหมู่ย่อย</label>
            <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="เช่น เครื่องวัดความดัน" style={{ width: '100%', padding: '10px' }} required />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
            <button type="submit" className={isEditing ? "btn-primary" : "btn-success"} disabled={loading}>
              {isEditing ? 'บันทึกแก้ไข' : 'เพิ่มหมวดหมู่ย่อย'}
            </button>
            {isEditing && <button type="button" className="btn-outline" onClick={resetForm}>ยกเลิก</button>}
          </div>
        </form>
      </div>

      <div className="card-box" style={{ padding: 0 }}>
        <table className="data-table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th style={{ width: '40%' }}>หมวดหมู่หลัก (ลำดับ)</th>
              <th style={{ width: '40%' }}>หมวดหมู่ย่อย</th>
              <th style={{ width: '20%', textAlign: 'center' }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedCategories).map((mainCat, index) => (
              <React.Fragment key={index}>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <td colSpan="3" style={{ fontWeight: 'bold', padding: '12px 15px' }}>
                    <span style={{ background: '#0ea5e9', color: '#fff', padding: '2px 8px', borderRadius: '4px', marginRight: '10px', fontSize: '0.8rem' }}>
                       ลำดับ {groupedCategories[mainCat][0].main_priority}
                    </span>
                    <FaTags color="#0ea5e9" /> {mainCat}
                  </td>
                </tr>
                {groupedCategories[mainCat].map(subCat => (
                  <tr key={subCat.id}>
                    <td style={{ color: '#94a3b8', paddingLeft: '40px' }}>↳ {mainCat}</td>
                    <td style={{ fontWeight: '500' }}>{subCat.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="icon-btn edit" onClick={() => startEdit(subCat)} style={{marginRight: '8px'}}><FaEdit /></button>
                      <button className="icon-btn delete" onClick={() => handleDelete(subCat.id)}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductManager() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // 🟢 1. ขยาย State formData ให้รองรับ image 1-4
  const [formData, setFormData] = useState({ 
    name: '', description: '', category: '', brand: '', model: '', 
    image: '', image2: '', image3: '', image4: '' 
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [lightboxSrc, setLightboxSrc] = useState('');

  const fetchProducts = useCallback(async () => {
    const { data, error } = await supabase.from('products').select('*').order('id');
    if (!error) setProducts(data);
  }, []);

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from('categories').select('*').order('name');
    if (data) {
        setCategories(data);
        if(data.length > 0 && !formData.category) setFormData(prev => ({...prev, category: data[0].name}));
    }
  }, [formData.category]);

  useEffect(() => { fetchProducts(); fetchCategories(); }, [fetchProducts, fetchCategories]);

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => { setSearchTerm(''); setSelectedCategory(''); };

  // 🟢 2. ปรับฟังก์ชันอัปโหลดให้รับ parameter ว่าอัปโหลดช่องไหน (imgKey)
  const handleImageUpload = async (e, imgKey) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${imgKey}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('product-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName);
      
      // อัปเดตเฉพาะฟิลด์ที่เลือก
      setFormData(prev => ({ ...prev, [imgKey]: publicUrl }));
    } catch (error) { 
      alert('Upload Error: ' + error.message); 
    } finally { 
      setUploading(false); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) {
      const { error } = await supabase.from('products').update(formData).eq('id', editId);
      if (!error) { alert('แก้ไขสำเร็จ'); resetForm(); fetchProducts(); }
    } else {
      const { error } = await supabase.from('products').insert([formData]);
      if (!error) { alert('เพิ่มสำเร็จ'); resetForm(); fetchProducts(); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ยืนยันการลบ?')) {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (!error) fetchProducts();
    }
  };

  const toggleRecommended = async (product) => {
    const newVal = !product.is_recommended;
    const { error } = await supabase.from('products').update({ is_recommended: newVal }).eq('id', product.id);
    if (!error) setProducts(products.map(p => p.id === product.id ? { ...p, is_recommended: newVal } : p));
  };

  const startEdit = (product) => {
    setFormData(product);
    setIsEditing(true);
    setEditId(product.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    const defaultCat = categories.length > 0 ? categories[0].name : '';
    setFormData({ 
      name: '', description: '', category: defaultCat, brand: '', model: '', 
      image: '', image2: '', image3: '', image4: '' 
    });
    setIsEditing(false);
    setEditId(null);
    setShowForm(false);
  };

  return (
    <div className="section-container">
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc('')} />

      <div className="section-header">
        <h2>จัดการสินค้า</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <FaTimes /> : <FaPlus />}
        </button>
      </div>

      {/* Filter Section */}
      <div className="filter-wrapper" style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="search-container" style={{ flex: 1, margin: 0 }}>
          <FaSearch />
          <input type="text" placeholder="ค้นหาชื่อสินค้า..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
        </div>
        <select className="search-input" style={{ width: 'auto', minWidth: '180px' }} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
          <option value="">ทั้งหมดทุกหมวดหมู่</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        {(searchTerm || selectedCategory) && <button className="btn-sm btn-outline" onClick={clearFilters}>ล้างค่า</button>}
      </div>
      
      {showForm && (
        <form onSubmit={handleSubmit}>
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', alignItems: 'start' }}>
    
    {/* 🟢 ฝั่งซ้าย: ข้อมูลพื้นฐานและรูปภาพหลัก */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      
      {/* ชื่อสินค้า */}
      <input 
        value={formData.name} 
        onChange={e => setFormData({...formData, name: e.target.value})} 
        placeholder="ชื่อสินค้า" 
        required 
        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
      />
      
      {/* หมวดหมู่ */}
      <select 
        value={formData.category} 
        onChange={e => setFormData({...formData, category: e.target.value})}
        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
      >
        <option value="" disabled>เลือกหมวดหมู่</option>
        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
      </select>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>ราคา (บาท)</label>
        <input 
          type="number" 
          step="0.01"
          value={formData.price || ''} 
          onChange={e => setFormData({...formData, price: e.target.value})} 
          placeholder="ระบุราคา (เว้นว่างไว้เพื่อแสดง 'ติดต่อสอบถาม')" 
          style={{ 
            width: '100%', 
            padding: '12px', 
            borderRadius: '8px', 
            border: '1px solid #cbd5e1',
            fontSize: '1rem'
          }} 
        />
      </div>
      {/* ยี่ห้อ และ รุ่น */}
      <div style={{ display: 'flex', gap: '10px' }}>
        <input 
          style={{ flex: 1, height: '45px', padding: '0 15px', fontSize: '1rem', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
          value={formData.brand || ''} 
          onChange={e => setFormData({...formData, brand: e.target.value})} 
          placeholder="ยี่ห้อ (Brand)" 
        />
        <input 
          style={{ flex: 1, height: '45px', padding: '0 15px', fontSize: '1rem', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
          value={formData.model || ''} 
          onChange={e => setFormData({...formData, model: e.target.value})} 
          placeholder="รุ่น (Model)" 
        />
      </div>

      {/* 🖼️ รูปภาพหลัก (เอามาถมช่องว่างฝั่งซ้าย) */}
      <div style={{ 
        border: '1px dashed #cbd5e1', 
        borderRadius: '12px', 
        padding: '20px', 
        background: '#f8fafc', 
        textAlign: 'center',
        minHeight: '260px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '10px' }}>รูปภาพหลัก (รูปที่ 1)</p>
        
        {formData.image && (
          <button
            type="button"
            onClick={() => setFormData({ ...formData, image: '' })}
            style={{ position: 'absolute', top: '10px', right: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '25px', height: '25px', cursor: 'pointer', zIndex: 2 }}
          >
            <FaTrash size={12} />
          </button>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {formData.image ? (
            <img src={formData.image} alt="Main" style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px', cursor: 'zoom-in' }} onClick={() => setLightboxSrc(formData.image)} />
          ) : (
            <FaImage size={40} color="#cbd5e1" />
          )}
          
          <label className="btn-upload" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'image')} disabled={uploading} style={{ display: 'none' }} />
            {uploading ? <FaSpinner className="fa-spin" /> : <FaUpload />} {formData.image ? 'เปลี่ยนรูปหลัก' : 'เลือกรูปหลัก'}
          </label>
          <input 
            type="text" 
            value={formData.image} 
            onChange={e => setFormData({...formData, image: e.target.value})} 
            placeholder="URL รูปภาพหลัก" 
            style={{ width: '100%', padding: '8px', fontSize: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
          />
        </div>
      </div>
    </div>

    {/* 🔵 ฝั่งขวา: รายละเอียดสินค้าขยายใหญ่ */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>รายละเอียด / คุณสมบัติสินค้า</label>
      <textarea 
        value={formData.description} 
        onChange={e => setFormData({...formData, description: e.target.value})} 
        placeholder="พิมพ์รายละเอียดสินค้าที่นี่..." 
        required 
        style={{ 
          width: '100%', 
          height: '465px', // ปรับความสูงให้พอดีกับฝั่งซ้ายรวมรูปหลัก
          padding: '20px',
          fontSize: '16px',
          lineHeight: '1.6',
          borderRadius: '10px',
          border: '2px solid #0ea5e9',
          resize: 'none' // ปิด resize เพื่อให้ Layout นิ่ง
        }}
      />
    </div>
  </div>

  {/* 🟡 ส่วนล่าง: รูปภาพประกอบ 2, 3, 4 */}
  <div style={{ marginTop: '25px' }}>
    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>รูปภาพเพิ่มเติม</label>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
      {['image2', 'image3', 'image4'].map((imgKey, idx) => (
        <div key={imgKey} style={{ border: '1px dashed #cbd5e1', padding: '15px', borderRadius: '8px', textAlign: 'center', position: 'relative', background: '#fcfcfc' }}>
          <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px' }}>รูปที่ {idx + 2}</p>
          {formData[imgKey] && (
            <button type="button" onClick={() => setFormData({ ...formData, [imgKey]: '' })} style={{ position: 'absolute', top: '5px', right: '5px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}>×</button>
          )}
          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, imgKey)} style={{ fontSize: '0.7rem', width: '100%', marginBottom: '5px' }} />
          {formData[imgKey] ? (
            <img src={formData[imgKey]} style={{ height: '60px', objectFit: 'contain' }} alt="extra" />
          ) : (
            <FaImage color="#eee" size={20} />
          )}
        </div>
      ))}
    </div>
  </div>

  <button type="submit" className="btn-success" disabled={uploading} style={{ marginTop: '20px', width: '100%', padding: '15px', fontSize: '1.1rem' }}>
    <FaSave /> บันทึกข้อมูลสินค้าทั้งหมด
  </button>
</form>
      )}

      {/* Table Section */}
      <table className="data-table">
        <thead><tr><th>แนะนำ</th><th>รูปหลัก</th><th>ชื่อ / ยี่ห้อ / รุ่น</th><th>หมวด</th><th>จัดการ</th></tr></thead>
        <tbody>
          {filteredProducts.length > 0 ? (
            filteredProducts.map(p => (
              <tr key={p.id}>
                <td style={{textAlign:'center'}}>
                  <button onClick={() => toggleRecommended(p)} style={{border:'none', background:'none', cursor:'pointer'}}>
                    {p.is_recommended ? <FaStar color="#f59e0b" /> : <FaRegStar color="#ccc" />}
                  </button>
                </td>
                <td>
                  <img src={p.image} alt="" className="thumb-img" style={{cursor:'zoom-in'}} onClick={() => setLightboxSrc(p.image)} />
                </td>
                <td>
                  <div style={{ fontWeight: 'bold' }}>{p.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {p.brand} {p.model ? `| ${p.model}` : ''}
                  </div>
                  {/* แสดงจุดเล็กๆ บอกจำนวนรูปที่มี */}
                  <div style={{ display: 'flex', gap: '3px', marginTop: '5px' }}>
                    {[p.image, p.image2, p.image3, p.image4].filter(x => x).map((_, i) => (
                      <span key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#0ea5e9' }}></span>
                    ))}
                  </div>
                </td>
                <td><span className="badge-gray">{p.category}</span></td>
                <td>
                  <button className="icon-btn edit" onClick={() => startEdit(p)}><FaEdit /></button>
                  <button className="icon-btn delete" onClick={() => handleDelete(p.id)}><FaTrash /></button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>ไม่พบสินค้า</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function CertificateManager() {
  const [certs, setCerts] = useState([]);
  const [formData, setFormData] = useState({ title: '', image: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  // [เพิ่ม] State สำหรับ Lightbox
  const [lightboxSrc, setLightboxSrc] = useState('');

  const fetchCerts = useCallback(async () => {
    const { data, error } = await supabase.from('certificates').select('*').order('id');
    if (!error) setCerts(data);
  }, []);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileName = `cert-${Date.now()}.${fileExt}`;
      const { error } = await supabase.storage.from('product-images').upload(fileName, file); 
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, image: publicUrl }));
    } catch (error) { alert('Upload Error: ' + error.message); } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) {
      const { error } = await supabase.from('certificates').update(formData).eq('id', editId);
      if (!error) { alert('แก้ไขสำเร็จ'); resetForm(); fetchCerts(); }
    } else {
      const { error } = await supabase.from('certificates').insert([formData]);
      if (!error) { alert('เพิ่มสำเร็จ'); resetForm(); fetchCerts(); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ยืนยันลบ?')) {
      const { error } = await supabase.from('certificates').delete().eq('id', id);
      if (!error) fetchCerts();
    }
  };

  const resetForm = () => { setFormData({ title: '', image: '' }); setIsEditing(false); setEditId(null); setShowForm(false); };

  return (
    <div className="section-container">
      {/* [เพิ่ม] Lightbox */}
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc('')} />

      <div className="section-header"><h2>จัดการใบรับรอง</h2><button className="btn-primary" onClick={() => setShowForm(!showForm)}>{showForm ? <FaTimes /> : <FaPlus />}</button></div>
      {showForm && (
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="ชื่อใบรับรอง" required />
            <div className="image-upload-area">
              <label className="btn-upload" style={{cursor:'pointer'}}>
                <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} style={{display:'none'}} />
                {uploading ? <FaSpinner className="fa-spin" /> : <FaUpload />} เลือกรูปจากเครื่อง
              </label>
              {/* [แก้ไข] รูป preview กดดูเต็มได้ */}
              {formData.image
                ? <img src={formData.image} alt="preview" className="thumb-img" style={{cursor:'zoom-in'}} onClick={() => setLightboxSrc(formData.image)} />
                : <FaImage />
              }
            </div>
            <button type="submit" className="btn-success" disabled={uploading}>บันทึก</button>
          </form>
        </div>
      )}
      <table className="data-table">
        <thead><tr><th>ตัวอย่าง</th><th>ชื่อ</th><th>จัดการ</th></tr></thead>
        <tbody>
          {certs.map(c => (
            <tr key={c.id}>
              {/* [แก้ไข] กดรูปเพื่อเปิด Lightbox */}
              <td><img src={c.image} alt="" className="thumb-img" style={{cursor:'zoom-in'}} onClick={() => setLightboxSrc(c.image)} /></td>
              <td>{c.title}</td>
              <td><button className="icon-btn edit" onClick={() => { setFormData(c); setIsEditing(true); setEditId(c.id); setShowForm(true); }}><FaEdit /></button><button className="icon-btn delete" onClick={() => handleDelete(c.id)}><FaTrash /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY_NEWS_FORM = { title: '', description: '', date: '', images: [] };

function NewsManager() {
  const [newsList, setNewsList] = useState([]);
  const [formData, setFormData] = useState(EMPTY_NEWS_FORM);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  // ความคืบหน้าตอนอัปโหลดหลายรูปพร้อมกัน
  const [uploadInfo, setUploadInfo] = useState({ done: 0, total: 0 });
  // [เพิ่ม] State สำหรับ Lightbox
  const [lightboxSrc, setLightboxSrc] = useState('');

  const fetchNews = useCallback(async () => {
    const { data, error } = await supabase.from('news_activities').select('*').order('id', { ascending: false });
    if (!error) setNewsList(data);
  }, []);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  // 🟢 อัปโหลดได้ทีละหลายไฟล์ ทุกไฟล์จะถูกต่อท้าย array เดิม
  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // เคลียร์เพื่อให้เลือกไฟล์เดิมซ้ำได้
    if (files.length === 0) return;

    setUploading(true);
    setUploadInfo({ done: 0, total: files.length });
    let done = 0;

    const results = await Promise.allSettled(files.map(async (file, i) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `news-${Date.now()}-${i}-${Math.round(Math.random() * 1e6)}.${fileExt}`;
      const { error } = await supabase.storage.from('product-images').upload(fileName, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(fileName);
      done += 1;
      setUploadInfo({ done, total: files.length });
      return publicUrl;
    }));

    const uploaded = results.filter(r => r.status === 'fulfilled').map(r => r.value);
    const failed = results.filter(r => r.status === 'rejected');

    if (uploaded.length > 0) {
      setFormData(prev => ({ ...prev, images: [...prev.images, ...uploaded] }));
    }
    if (failed.length > 0) {
      alert(`อัปโหลดไม่สำเร็จ ${failed.length} ไฟล์\n${failed[0].reason?.message || ''}`);
    }

    setUploading(false);
    setUploadInfo({ done: 0, total: 0 });
  };

  // จัดการรูปในรายการ: ลบ / สลับตำแหน่ง / ตั้งเป็นรูปปก
  const removeImage = (idx) =>
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const moveImage = (idx, dir) => setFormData(prev => {
    const next = [...prev.images];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return prev;
    [next[idx], next[target]] = [next[target], next[idx]];
    return { ...prev, images: next };
  });

  const makeCover = (idx) => setFormData(prev => {
    if (idx === 0) return prev;
    const next = [...prev.images];
    const [picked] = next.splice(idx, 1);
    return { ...prev, images: [picked, ...next] };
  });

  // บันทึกลง DB — ถ้ายังไม่ได้เพิ่มคอลัมน์ images จะ fallback ไปบันทึกแบบเดิม
  const saveNews = async (payload) => {
    const run = (data) => isEditing
      ? supabase.from('news_activities').update(data).eq('id', editId)
      : supabase.from('news_activities').insert([data]);

    let { error } = await run(payload);

    if (error && /images/i.test(error.message || '')) {
      const legacy = { ...payload };
      delete legacy.images;
      const retry = await run(legacy);
      if (!retry.error) {
        alert('บันทึกแล้ว แต่เก็บได้เฉพาะรูปปก\n\nตาราง news_activities ยังไม่มีคอลัมน์ images\nกรุณารัน SQL migration ก่อน แล้วบันทึกข่าวนี้ใหม่อีกครั้ง');
        return true;
      }
      error = retry.error;
    }

    if (error) { alert('บันทึกไม่สำเร็จ: ' + error.message); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) { alert('กรุณาเลือกรูปอย่างน้อย 1 รูป'); return; }

    const payload = {
      title: formData.title,
      description: formData.description,
      date: formatThaiDate(formData.date),
      image: formData.images[0],   // รูปปก เก็บไว้เพื่อให้ข้อมูลเดิมยังใช้ได้
      images: formData.images
    };

    if (await saveNews(payload)) { resetForm(); fetchNews(); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('ยืนยันลบ?')) {
      const { error } = await supabase.from('news_activities').delete().eq('id', id);
      if (!error) fetchNews();
    }
  };

  const startEdit = (n) => {
    setFormData({
      title: n.title || '',
      description: n.description || '',
      date: '',
      images: getNewsImages(n)
    });
    setIsEditing(true);
    setEditId(n.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData(EMPTY_NEWS_FORM);
    setIsEditing(false);
    setEditId(null);
    setShowForm(false);
  };

  return (
    <div className="section-container">
      {/* [เพิ่ม] Lightbox */}
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc('')} />

      <div className="section-header">
        <h2>จัดการข่าวสาร</h2>
        <button className="btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>{showForm ? <FaTimes /> : <FaPlus />}</button>
      </div>

      {showForm && (
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="หัวข้อ" required />
            <div style={{display:'flex', flexDirection:'column', gap:'4px'}}>
              <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
              {formData.date && (
                <small style={{color:'#555'}}>จะบันทึกเป็น: <strong>{formatThaiDate(formData.date)}</strong></small>
              )}
            </div>
            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="รายละเอียด" required />

            {/* 🟢 ส่วนจัดการรูปภาพหลายรูป */}
            <div style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '15px', marginTop: '10px', background: '#f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155' }}>
                  <FaImages /> รูปภาพข่าว ({formData.images.length} รูป)
                </strong>
                <label className="btn-upload" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                  <input type="file" accept="image/*" multiple onChange={handleImagesUpload} disabled={uploading} style={{ display: 'none' }} />
                  {uploading
                    ? <><FaSpinner className="fa-spin" /> กำลังอัปโหลด {uploadInfo.done}/{uploadInfo.total}</>
                    : <><FaUpload /> เพิ่มรูป (เลือกได้หลายไฟล์)</>}
                </label>
              </div>

              {formData.images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                  <FaImage size={36} style={{ opacity: 0.5 }} />
                  <p style={{ margin: '10px 0 0' }}>ยังไม่มีรูป — กดปุ่มด้านบนเพื่อเลือกรูป (เลือกพร้อมกันหลายไฟล์ได้)</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                  {formData.images.map((img, idx) => (
                    <div key={`${img}-${idx}`} style={{ position: 'relative', border: idx === 0 ? '2px solid #2563eb' : '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
                      {idx === 0 && (
                        <span style={{ position: 'absolute', top: '6px', left: '6px', background: '#2563eb', color: 'white', fontSize: '0.7rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px', zIndex: 2 }}>รูปปก</span>
                      )}
                      <button type="button" onClick={() => removeImage(idx)} title="ลบรูปนี้"
                        style={{ position: 'absolute', top: '6px', right: '6px', width: '26px', height: '26px', borderRadius: '50%', border: 'none', background: 'rgba(220,38,38,0.9)', color: 'white', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaTimes size={12} />
                      </button>

                      <img src={img} alt={`news-${idx}`} onClick={() => setLightboxSrc(img)}
                        style={{ width: '100%', height: '110px', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }} />

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px', gap: '4px', borderTop: '1px solid #f1f5f9' }}>
                        <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} title="เลื่อนไปทางซ้าย"
                          style={{ border: 'none', background: 'transparent', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, color: '#475569' }}>
                          <FaArrowLeft size={12} />
                        </button>
                        <button type="button" onClick={() => makeCover(idx)} disabled={idx === 0} title="ตั้งเป็นรูปปก"
                          style={{ border: 'none', background: 'transparent', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, color: '#f59e0b', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {idx === 0 ? <FaStar size={12} /> : <FaRegStar size={12} />} ปก
                        </button>
                        <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === formData.images.length - 1} title="เลื่อนไปทางขวา"
                          style={{ border: 'none', background: 'transparent', cursor: idx === formData.images.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === formData.images.length - 1 ? 0.3 : 1, color: '#475569' }}>
                          <FaArrowRight size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="btn-success" disabled={uploading} style={{ marginTop: '15px' }}>
              <FaSave /> {isEditing ? 'บันทึกการแก้ไข' : 'บันทึก'}
            </button>
          </form>
        </div>
      )}

      <table className="data-table">
        <thead><tr><th>รูป</th><th>วันที่</th><th>หัวข้อ</th><th>จัดการ</th></tr></thead>
        <tbody>
          {newsList.map(n => {
            const imgs = getNewsImages(n);
            return (
              <tr key={n.id}>
                {/* [แก้ไข] กดรูปเพื่อเปิด Lightbox + แสดงจำนวนรูปทั้งหมด */}
                <td>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={imgs[0]} alt="" className="thumb-img" style={{cursor:'zoom-in'}} onClick={() => setLightboxSrc(imgs[0])} />
                    {imgs.length > 1 && (
                      <span style={{ position: 'absolute', bottom: '2px', right: '2px', background: 'rgba(15,23,42,0.85)', color: 'white', fontSize: '0.65rem', fontWeight: 'bold', padding: '1px 6px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <FaImages size={9} /> {imgs.length}
                      </span>
                    )}
                  </div>
                </td>
                <td>{n.date}</td>
                <td>{n.title}</td>
                <td>
                  <button className="icon-btn edit" onClick={() => startEdit(n)}><FaEdit /></button>
                  <button className="icon-btn delete" onClick={() => handleDelete(n.id)}><FaTrash /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MessageViewer() {
  const [messages, setMessages] = useState([]);
  const fetchMessages = useCallback(async () => {
    const { data } = await supabase.from('contact_messages').select('*').order('created_at', { ascending: false });
    if (data) setMessages(data);
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const toggleRead = async (id, status) => {
    await supabase.from('contact_messages').update({ is_read: !status }).eq('id', id);
    fetchMessages();
  };
  const handleDelete = async (id) => {
    if (window.confirm('ลบข้อความ?')) { await supabase.from('contact_messages').delete().eq('id', id); fetchMessages(); }
  };

  return (
    <div className="section-container">
      <div className="section-header"><h2>ข้อความจากลูกค้า</h2></div>
      <div className="messages-list">
        {messages.length === 0 ? <p className="no-data">ไม่มีข้อความ</p> : messages.map(msg => (
          <div key={msg.id} className={`message-card ${msg.is_read ? 'read' : 'unread'}`}>
            <div className="msg-header"><div><h4>{msg.subject}</h4><span className="msg-sender">{msg.name} ({msg.email}) | {msg.phone}</span></div><span className="msg-date">{new Date(msg.created_at).toLocaleString('th-TH')}</span></div>
            <div className="msg-body"><p>{msg.message}</p></div>
            <div className="msg-footer"><button className={`btn-sm ${msg.is_read ? 'btn-outline' : 'btn-success'}`} onClick={() => toggleRead(msg.id, msg.is_read)}>{msg.is_read ? <><FaEnvelopeOpen /> อ่านแล้ว</> : <><FaCheck /> ทำเครื่องหมายว่าอ่านแล้ว</>}</button><button className="btn-sm btn-danger" onClick={() => handleDelete(msg.id)}><FaTrash /> ลบ</button></div>
          </div>
        ))}
      </div>
    </div>
  );
}